import React, { Component } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import "./stateful_button.less";

import Spinner from "@ui/spinner";

import { makeCancellable, PromiseCancelledException } from "@utils/promise";

const isConfigObject = PropTypes.shape({
  svg: PropTypes.element,
  text: PropTypes.string
});

const BUTTON_STATES = {
  ERROR: "error",
  INIT: "init",
  PENDING: "pending",
  SUCCESS: "success"
};

export default class StatefulButton extends Component {
  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    errorConfig: isConfigObject,
    initConfig: isConfigObject,
    onClick: PropTypes.func.isRequired,
    onSuccess: PropTypes.func.isRequired,
    refreshTimeout: PropTypes.number,
    successConfig: isConfigObject,
    svgPosition: PropTypes.oneOf(["left", "right"])
  }

  static defaultProps = {
    refreshTimeout: 2000,
    svgPosition: "left"
  };

  state = {
    buttonState: BUTTON_STATES.INIT
  };

  componentDidUpdate() {
    clearTimeout(this.timeout);
  }

  componentWillUnmount() {
    clearTimeout(this.timeout);
    this.cancellablePromise && this.cancellablePromise.cancel();
  }

  onClick = async () => {
    const {
      onClick,
      onSuccess,
      refreshTimeout
    } = this.props;

    try {
      let promise = onClick();
      if (!(promise instanceof Promise)) {
        promise = Promise.resolve();
      }

      this.setState({ buttonState: BUTTON_STATES.PENDING });
      this.cancellablePromise = makeCancellable(promise);
      const result = await this.cancellablePromise.promise;

      if (onSuccess) {
        onSuccess(result);
      }

      this.setState({ buttonState: BUTTON_STATES.SUCCESS });
    } catch (error) {
      if (error && error instanceof PromiseCancelledException) {
        return;
      }
      this.setState({ buttonState: BUTTON_STATES.ERROR });
    }

    this.timeout = setTimeout(() => {
      this.setState({ buttonState: BUTTON_STATES.INIT });
    }, refreshTimeout);
  }

  get buttonConfig() {
    const { buttonState } = this.state;
    const {
      errorConfig,
      initConfig,
      successConfig
    } = this.props;

    const stateConfig = (() => {
      switch (buttonState) {
        case BUTTON_STATES.ERROR: return errorConfig;
        case BUTTON_STATES.INIT: return initConfig;
        case BUTTON_STATES.SUCCESS: return successConfig;
        default: return {};
      }
    })();

    return {
      ...initConfig,
      ...stateConfig
    };
  }

  render() {
    const { buttonConfig } = this;
    const { buttonState } = this.state;
    const { className, children: propsText, svgPosition } = this.props;

    const icon = (() => {
      switch (buttonState) {
        case BUTTON_STATES.PENDING:
          return (
            <Spinner small />
          );
        default:
          return buttonConfig.svg;
      }
    })();

    const buttonClassName = classNames(
      "StatefulButton",
      icon && `StatefulButton--icon-${svgPosition}`,
      `StatefulButton--${buttonState}`,
      className,
    );

    return (
      <button
        type="button"
        className={buttonClassName}
        onClick={this.onClick}
        disabled={buttonState === BUTTON_STATES.PENDING}
      >
        {icon}
        <span className={classNames({ invisible: buttonState === BUTTON_STATES.PENDING })}>
          {buttonConfig.text || propsText}
        </span>
      </button>
    );
  }
}
